import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Users, Plus, Search, ClipboardList, Phone, Mail, MapPin, X, Archive, ArchiveRestore, Trash2, AlertTriangle, Eye, MailPlus, Ban, CircleCheck, Pencil, Wrench, Crown, FileText, ScrollText, Bell } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { AddressLink } from '../../components/MapsNavProvider';
import { toast } from 'sonner';
import { startPortalPreview } from '../../lib/portalPreview';
import QuickAddCustomerDialog from '../../components/QuickAddCustomerDialog';
import CustomerEditDialog from '../../components/CustomerEditDialog';
import CommunicationHistory from '../../components/communication/CommunicationHistory';
import CustomerTabsBar from '../../components/CustomerTabsBar';
import CustomerServiceHistory from '../../components/CustomerServiceHistory';
import { fmtETDateTime } from '../../utils/tz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PORTAL_BADGES = {
  active:              { label: 'Active',             cls: 'bg-green-900/30 text-green-400 border-green-700/40' },
  pending_activation:  { label: 'Pending Activation', cls: 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40' },
  no_portal:           { label: 'No Portal',          cls: 'bg-[#262626] text-[#A3A3A3] border-[#3f3f3f]' },
  disabled:            { label: 'Disabled',           cls: 'bg-red-900/30 text-red-400 border-red-700/40' },
};

const PortalStatusBadge = ({ status }) => {
  const meta = PORTAL_BADGES[status] || PORTAL_BADGES.no_portal;
  return (
    <span data-testid={`portal-badge-${status || 'no_portal'}`}
      className={`text-[10px] tracking-wider uppercase px-2 py-0.5 rounded border font-bold ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

const EVENT_LABELS = {
  invoice_created: 'Invoice',
  invoice_payment: 'Payment',
  invoice_response: 'Invoice Response',
  estimate_created: 'Estimate',
  estimate_action: 'Estimate Response',
  service_request: 'Service Request',
  schedule_change: 'Scheduled',
  membership_change: 'Membership',
  note: 'Note',
  email: 'Email',
  sms: 'SMS',
  call: 'Call',
  tech_note: 'Tech Note',
  upload: 'Upload',
};

const Customers = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [customers, setCustomers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [portalFilter, setPortalFilter] = useState('');  // '', 'no_portal', 'pending_activation', 'active', 'disabled'
  const [portalCounts, setPortalCounts] = useState({ no_portal: 0, pending_activation: 0, active: 0, disabled: 0, total: 0 });

  // Timeline drawer
  const [openCust, setOpenCust] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [hubSummary, setHubSummary] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => { refresh(); refreshCounts(); }, [showArchived, portalFilter]);

  const refresh = async () => {
    const qp = new URLSearchParams();
    if (showArchived) qp.set('include_archived', 'true');
    if (portalFilter) qp.set('portal_status', portalFilter);
    const url = `${API}/admin/customers${qp.toString() ? '?' + qp.toString() : ''}`;
    try {
      const { data } = await axios.get(url, { withCredentials: true });
      setCustomers(data);
    } catch {
      // iter162 — permission denied / network — leave list untouched (likely a
      // preview-as user without `view_customers`). Toast is suppressed because
      // empty-state UI already communicates "no data".
      setCustomers([]);
    }
  };

  const refreshCounts = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/customers/portal-status-counts`, { withCredentials: true });
      setPortalCounts(data);
    } catch { /* non-fatal */ }
  };

  const resendInvite = async (e, c) => {
    e.stopPropagation();
    if (!c.email || c.email.endsWith('@noemail.local')) {
      toast.error('Customer has no email on file — add one before sending an invite.');
      return;
    }
    try {
      const { data } = await axios.post(`${API}/admin/customers/${c.id}/portal/resend-invite`, {}, { withCredentials: true });
      toast.success(`Invite re-sent → ${c.email}${data.sms?.sent ? ' + SMS' : ''}`);
      refresh(); refreshCounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to resend invite'); }
  };

  const disablePortal = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`Disable portal access for ${c.name}? They won't be able to sign in until you re-activate them.`)) return;
    try {
      await axios.post(`${API}/admin/customers/${c.id}/portal/disable`, {}, { withCredentials: true });
      toast.success('Portal access disabled');
      refresh(); refreshCounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const activatePortal = async (e, c) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post(`${API}/admin/customers/${c.id}/portal/activate`, {}, { withCredentials: true });
      toast.success(`Portal re-enabled → ${data.portal_status.replace('_', ' ')}`);
      refresh(); refreshCounts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const archive = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`Archive ${c.name}? They'll be hidden from default lists. Their invoices and history stay intact.`)) return;
    try {
      await axios.patch(`${API}/admin/customer/${c.id}/archive`, {}, { withCredentials: true });
      toast.success('Archived');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const unarchive = async (e, c) => {
    e.stopPropagation();
    try {
      await axios.patch(`${API}/admin/customer/${c.id}/unarchive`, {}, { withCredentials: true });
      toast.success('Restored');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const hardDelete = async (e, c) => {
    e.stopPropagation();
    if (!window.confirm(`HARD DELETE ${c.name}? This permanently removes them. Blocked if they have any invoices, estimates, memberships, or history.`)) return;
    try {
      await axios.delete(`${API}/admin/customer/${c.id}/hard?confirm=DELETE`, { withCredentials: true });
      toast.success('Deleted permanently');
      refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'HAS_LINKED_RECORDS') {
        const linked = Object.entries(d.linked || {}).map(([k, v]) => `${k}:${v}`).join(', ');
        toast.error(`Cannot delete — linked: ${linked}. Archive instead.`);
      } else {
        toast.error(typeof d === 'string' ? d : 'Delete failed');
      }
    }
  };

  const onCustomerCreated = (data) => {
    refresh();
    refreshCounts();
    // Auto-open the new customer's timeline so the user lands on a useful page.
    // iter249 — pass the FULL canonical record (create response is now complete)
    // so the profile shows name/email/phone/address/category, not a partial stub.
    if (data && data.id && !data.existing) {
      openTimeline(data);
    }
  };

  const [smsOptOut, setSmsOptOut] = useState(false);
  const [smsHistory, setSmsHistory] = useState([]);
  // iter74 — edit dialog target (drawer-level so the gold Edit button below the timeline header opens it)
  const [editCust, setEditCust] = useState(null);
  const [custAssets, setCustAssets] = useState(null);   // canonical db.assets for the open customer
  const canEdit = ['super_admin', 'admin', 'manager', 'service_manager', 'construction_manager', 'office_staff'].includes(user?.role);

  const openTimeline = async (c) => {
    setOpenCust(c);
    setSmsOptOut(!!c.sms_opt_out);
    setHubSummary(null);
    setCustAssets(null);
    axios.get(`${API}/admin/customers/${c.id}/hub-summary`, { withCredentials: true })
      .then(({ data }) => setHubSummary(data))
      .catch(() => setHubSummary(null));
    axios.get(`${API}/customers/${c.id}/assets`, { withCredentials: true })
      .then(({ data }) => setCustAssets(data))
      .catch(() => setCustAssets([]));
    const { data } = await axios.get(`${API}/customers/${c.id}/timeline`, { withCredentials: true });
    setTimeline(data);
    // Pull SMS history in parallel; never block on failure. Larger limit so skipped rows are surfaced.
    axios.get(`${API}/admin/customers/${c.id}/sms-history?limit=200`, { withCredentials: true })
      .then(({ data }) => setSmsHistory(data.history || []))
      .catch(() => setSmsHistory([]));
  };

  // iter198 — /admin/customers?open=<id> auto-opens that customer's drawer
  // (used by "View Customer" quick actions from membership rows).
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId || customers.length === 0) return;
    const target = customers.find((c) => c.id === openId);
    if (target) {
      openTimeline(target);
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers, searchParams]);

  const toggleSmsOptOut = async () => {    const next = !smsOptOut;
    setSmsOptOut(next);
    try {
      await axios.patch(`${API}/admin/customers/${openCust.id}/sms-opt-out`, { opt_out: next }, { withCredentials: true });
      toast.success(next ? 'Marketing SMS paused for this customer.' : 'Marketing SMS re-enabled.');
    } catch (err) {
      setSmsOptOut(!next);
      toast.error('Failed to update');
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await axios.post(`${API}/customers/${openCust.id}/timeline/note`, { note: noteText.trim() }, { withCredentials: true });
      setNoteText('');
      openTimeline(openCust);
      toast.success('Note added');
    } catch { toast.error('Failed'); }
  };

  const filtered = search ? customers.filter(c => `${c.name} ${c.email} ${c.phone} ${c.address}`.toLowerCase().includes(search.toLowerCase())) : customers;

  return (
    <div data-testid="admin-customers-page" className="space-y-4">
      <CustomerTabsBar userRole={user?.role} />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Customers</h2>
          <p className="text-[#A3A3A3]">All customers — click any row to see their full history</p>
        </div>
        <Button data-testid="new-customer-btn" onClick={() => setShowForm(!showForm)} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-2" /> New Customer</Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 text-[#A3A3A3] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, phone, email, address" className="pl-10 bg-[#0A0A0A] border-[#262626] text-white" />
        </div>
        <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
          <input
            data-testid="customers-show-archived"
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="accent-[#D4AF37]"
          />
          Show archived
        </label>
      </div>

      {/* Portal status filter chips */}
      <div data-testid="portal-status-filter" className="flex items-center gap-2 flex-wrap">
        <span className="text-[#A3A3A3] text-xs uppercase tracking-wider mr-1">Portal</span>
        {[
          { key: '', label: 'All', count: portalCounts.total },
          { key: 'active', label: 'Active', count: portalCounts.active },
          { key: 'pending_activation', label: 'Pending Activation', count: portalCounts.pending_activation },
          { key: 'no_portal', label: 'No Portal', count: portalCounts.no_portal },
          { key: 'disabled', label: 'Disabled', count: portalCounts.disabled },
        ].map((f) => (
          <button
            key={f.key || 'all'}
            data-testid={`portal-filter-${f.key || 'all'}`}
            onClick={() => setPortalFilter(f.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
              portalFilter === f.key
                ? 'bg-[#D4AF37] border-[#D4AF37] text-[#0A0A0A]'
                : 'bg-[#0A0A0A] border-[#262626] text-[#A3A3A3] hover:border-[#3f3f3f] hover:text-white'
            }`}
          >
            {f.label} <span className="opacity-70">({f.count})</span>
          </button>
        ))}
      </div>

      {showForm && (
        <QuickAddCustomerDialog
          open={showForm}
          onOpenChange={setShowForm}
          onCreated={onCustomerCreated}
        />
      )}

      <div className="grid gap-3">
        {filtered.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No customers found</div>
        ) : filtered.map((c) => (
          <div
            key={c.id}
            data-testid={`customer-row-${c.id}`}
            className={`bg-[#141414] border ${c.archived ? 'border-[#262626] opacity-60' : 'border-[#262626] hover:border-[#D4AF37]'} rounded-md p-4 flex items-center gap-4 text-left transition-colors`}
          >
            <button onClick={() => openTimeline(c)} className="flex items-center gap-4 flex-1 min-w-0 text-left">
              <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-md flex items-center justify-center shrink-0">
                <Users className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-medium truncate">{c.name}</p>
                  {c.archived && <Badge className="bg-amber-900/30 text-amber-400 border-amber-700 text-[10px]">ARCHIVED</Badge>}
                  <PortalStatusBadge status={c.portal_status} />
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[#A3A3A3] text-xs mt-1">
                  {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                  {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                  {c.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.address}</span>}
                </div>
              </div>
              <span className="flex items-center gap-1.5 shrink-0 text-[#D4AF37]" title="View Customer History" data-testid={`customer-history-icon-${c.id}`}>
                <ClipboardList className="w-5 h-5" />
                <span className="hidden lg:inline text-xs uppercase tracking-wider font-bold">History</span>
              </span>
            </button>
            <div className="flex items-center gap-1 shrink-0">
              {/* iter74 — quick edit access from the list, mirrors drawer Edit */}
              {canEdit && (
                <Button
                  data-testid={`customer-edit-row-${c.id}`}
                  onClick={(e) => { e.stopPropagation(); setEditCust(c); }}
                  size="sm"
                  variant="outline"
                  className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-9 px-3"
                  title="Edit customer info"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {/* Portal actions */}
              {c.portal_status === 'disabled' ? (
                <Button
                  data-testid={`portal-activate-${c.id}`}
                  onClick={(e) => activatePortal(e, c)}
                  size="sm"
                  variant="outline"
                  className="border-green-700/40 text-green-400 hover:bg-green-900/20 h-9 px-3"
                  title="Re-enable portal access"
                >
                  <CircleCheck className="w-4 h-4 mr-1" /> Enable
                </Button>
              ) : (c.portal_status === 'pending_activation' || c.portal_status === 'no_portal') && c.email && !c.email.endsWith('@noemail.local') ? (
                <Button
                  data-testid={`portal-resend-${c.id}`}
                  onClick={(e) => resendInvite(e, c)}
                  size="sm"
                  variant="outline"
                  className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-9 px-3"
                  title="Re-send portal invite"
                >
                  <MailPlus className="w-4 h-4 mr-1" /> Invite
                </Button>
              ) : null}
              {c.portal_status === 'active' && (
                <Button
                  data-testid={`portal-disable-${c.id}`}
                  onClick={(e) => disablePortal(e, c)}
                  size="sm"
                  variant="outline"
                  className="border-red-700/40 text-red-400 hover:bg-red-900/20 h-9 px-3"
                  title="Disable portal access"
                >
                  <Ban className="w-4 h-4" />
                </Button>
              )}
              <Button
                data-testid={`customer-view-side-${c.id}`}
                onClick={(e) => { e.stopPropagation(); startPortalPreview({ id: c.id, name: c.name, email: c.email, role: 'customer' }); }}
                size="sm"
                variant="outline"
                className="border-cyan-700/40 text-cyan-300 hover:bg-cyan-900/20 h-9 px-3"
                title="View Their Side (read-only preview)"
              >
                <Eye className="w-4 h-4 mr-1" /> View Their Side
              </Button>
              {c.archived ? (
                <Button
                  data-testid={`customer-unarchive-${c.id}`}
                  onClick={(e) => unarchive(e, c)}
                  size="sm"
                  variant="outline"
                  className="border-green-700/40 text-green-400 hover:bg-green-700/10 h-9 px-3"
                  title="Restore"
                >
                  <ArchiveRestore className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  data-testid={`customer-archive-${c.id}`}
                  onClick={(e) => archive(e, c)}
                  size="sm"
                  variant="outline"
                  className="border-[#262626] text-amber-400 hover:bg-amber-900/20 h-9 px-3"
                  title="Archive"
                >
                  <Archive className="w-4 h-4" />
                </Button>
              )}
              {isSuperAdmin && (
                <Button
                  data-testid={`customer-delete-${c.id}`}
                  onClick={(e) => hardDelete(e, c)}
                  size="sm"
                  variant="outline"
                  className="border-red-700/40 text-red-400 hover:bg-red-900/20 h-9 px-3"
                  title="Hard delete (super_admin only)"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Sheet open={!!openCust} onOpenChange={(o) => !o && setOpenCust(null)}>
        <SheetContent className="bg-[#141414] border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto">
          {openCust && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[#D4AF37] flex items-center justify-between gap-3">
                  <span>{openCust.name} — Timeline</span>
                  {canEdit && (
                    <Button
                      data-testid={`customer-edit-${openCust.id}`}
                      onClick={() => setEditCust(openCust)}
                      size="sm"
                      variant="outline"
                      className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8"
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                  )}
                </SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-4">
                <div className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 text-sm" data-testid="customer-profile-details">
                  <p className="text-[#A3A3A3]">{(openCust.email && !String(openCust.email).endsWith('@noemail.local')) ? openCust.email : <span className="text-red-400/80">No email on file</span>} {openCust.phone && `• ${openCust.phone}`}</p>
                  {openCust.company_name && <p className="text-[#C9C9C9] text-xs mt-1" data-testid="customer-profile-company">{openCust.company_name}</p>}
                  <div className="grid grid-cols-1 gap-1.5 mt-2 pt-2 border-t border-[#262626] text-xs">
                    {openCust.address && <div><span className="text-[#525252] uppercase text-[10px] tracking-wider">Service</span><br /><AddressLink address={openCust.address} testId="customer-profile-address" className="text-[#C9C9C9]" showIcon={false} /></div>}
                    {openCust.billing_address && openCust.billing_address !== openCust.address && <div><span className="text-[#525252] uppercase text-[10px] tracking-wider">Billing</span><br /><span className="text-[#C9C9C9]" data-testid="customer-profile-billing">{openCust.billing_address}</span></div>}
                    <div className="flex flex-wrap gap-2 mt-1">
                      {openCust.customer_type && <span className="px-2 py-0.5 rounded bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-[10px] font-bold uppercase tracking-wider" data-testid="customer-profile-category">{String(openCust.customer_type).replace(/_/g, ' ')}</span>}
                      {openCust.portal_status && <span className="px-2 py-0.5 rounded bg-[#262626] border border-[#404040] text-[#A3A3A3] text-[10px] font-bold uppercase tracking-wider" data-testid="customer-profile-portal">{String(openCust.portal_status).replace(/_/g, ' ')}</span>}
                    </div>
                    {openCust.notes && <div className="mt-1"><span className="text-[#525252] uppercase text-[10px] tracking-wider">Notes</span><br /><span className="text-[#C9C9C9]" data-testid="customer-profile-notes">{openCust.notes}</span></div>}
                  </div>
                  <label className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-[#262626] text-xs">
                    <span className="text-[#A3A3A3]">SMS Opt-Out (marketing & follow-ups)</span>
                    <input
                      data-testid={`customer-sms-opt-out-${openCust.id}`}
                      type="checkbox"
                      checked={smsOptOut}
                      onChange={toggleSmsOptOut}
                      className="accent-[#D4AF37] w-4 h-4 cursor-pointer"
                    />
                  </label>
                  <p className="text-[10px] text-[#525252] mt-1">Transactional messages (appointment confirm, on-the-way, portal invite) always send.</p>
                </div>

                {/* iter198 — Customer hub summary cards + Add Equipment. */}
                <div data-testid="customer-hub-summary" className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider">Snapshot</p>
                    <Button
                      data-testid="customer-add-equipment"
                      onClick={() => navigate(`/admin/assets?customer_id=${openCust.id}&add=1`)}
                      size="sm"
                      className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] h-8"
                    >
                      <Wrench className="w-3.5 h-3.5 mr-1" /> Add Equipment
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div data-testid="hub-card-membership" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                      <p className="text-[10px] uppercase tracking-wider text-[#737373] flex items-center gap-1"><Crown className="w-3 h-3" /> Membership</p>
                      <p className={`text-sm font-bold mt-0.5 ${hubSummary?.membership?.active ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'}`}>
                        {hubSummary ? (hubSummary.membership.active ? `${hubSummary.membership.tier?.toUpperCase()} Active` : 'None') : '…'}
                      </p>
                    </div>
                    <div data-testid="hub-card-equipment" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                      <p className="text-[10px] uppercase tracking-wider text-[#737373] flex items-center gap-1"><Wrench className="w-3 h-3" /> Equipment</p>
                      <p className="text-sm font-bold mt-0.5 text-white">{hubSummary ? `${hubSummary.equipment_count} record${hubSummary.equipment_count === 1 ? '' : 's'}` : '…'}</p>
                    </div>
                    <div data-testid="hub-card-unpaid" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                      <p className="text-[10px] uppercase tracking-wider text-[#737373] flex items-center gap-1"><FileText className="w-3 h-3" /> Unpaid Inv</p>
                      <p className={`text-sm font-bold mt-0.5 ${hubSummary?.unpaid_invoices ? 'text-red-400' : 'text-white'}`}>{hubSummary ? hubSummary.unpaid_invoices : '…'}</p>
                    </div>
                    <div data-testid="hub-card-estimates" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                      <p className="text-[10px] uppercase tracking-wider text-[#737373] flex items-center gap-1"><ScrollText className="w-3 h-3" /> Open Est</p>
                      <p className="text-sm font-bold mt-0.5 text-white">{hubSummary ? hubSummary.open_estimates : '…'}</p>
                    </div>
                    <div data-testid="hub-card-followups" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                      <p className="text-[10px] uppercase tracking-wider text-[#737373] flex items-center gap-1"><Bell className="w-3 h-3" /> Follow-Ups</p>
                      <p className={`text-sm font-bold mt-0.5 ${hubSummary?.open_followups ? 'text-[#D4AF37]' : 'text-white'}`}>{hubSummary ? hubSummary.open_followups : '…'}</p>
                    </div>
                    <div data-testid="hub-card-service" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-2.5">
                      <p className="text-[10px] uppercase tracking-wider text-[#737373] flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Service</p>
                      <p className="text-xs mt-0.5 text-white leading-tight">
                        Last: {hubSummary?.last_service_date || '—'}<br />
                        Next: {hubSummary?.next_service_date || '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Inline canonical Equipment / Assets list (same db.assets as Field + Portal) */}
                {custAssets !== null && (
                  <div data-testid="customer-equipment-list" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3 space-y-2">
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> Equipment / Assets ({custAssets.length})</p>
                    {custAssets.length === 0 ? (
                      <p className="text-[#525252] text-xs italic">No equipment on file for this customer.</p>
                    ) : custAssets.map((a) => (
                      <div key={a.id} data-testid={`customer-asset-${a.id}`} className="bg-[#141414] border border-[#262626] rounded px-3 py-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="text-white text-sm font-medium">
                            {a.type || a.asset_type_key || 'Equipment'}
                            {(a.brand || a.model) && <span className="text-[#737373] text-xs font-normal"> · {[a.brand, a.model].filter(Boolean).join(' ')}</span>}
                          </p>
                          <div className="flex items-center gap-1.5">
                            {a.published === false && (
                              <span data-testid={`asset-pending-${a.id}`} className="text-[9px] font-bold uppercase tracking-wider text-amber-300 bg-amber-500/15 border border-amber-500/50 px-1.5 py-0.5 rounded">Pending Approval</span>
                            )}
                            {a.status && a.status !== 'active' && (
                              <span className="text-[9px] uppercase tracking-wider text-[#A3A3A3] bg-[#262626] border border-[#404040] px-1.5 py-0.5 rounded">{a.status}</span>
                            )}
                          </div>
                        </div>
                        <div className="text-[#A3A3A3] text-xs mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                          {a.serial && <span>S/N {a.serial}</span>}
                          {a.install_date && <span>Installed {String(a.install_date).slice(0, 10)}</span>}
                          {(() => {
                            const w = a.warranty || a.warranty_info || a.warranty_expiration;
                            const t = !w ? '' : (typeof w === 'object'
                              ? Object.values(w).filter((v) => typeof v === 'string' || typeof v === 'number').join(' / ')
                              : String(w));
                            return t ? <span>Warranty {t}</span> : null;
                          })()}
                          {a.last_service_date && <span>Last service {String(a.last_service_date).slice(0, 10)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* iter201 — Service History (completed visits + invoice groups) */}
                {openCust && <CustomerServiceHistory customerId={openCust.id} />}

                {smsHistory.length > 0 && (
                  <div data-testid="customer-sms-history" className="bg-[#0A0A0A] border border-[#262626] rounded-md p-3">
                    <CommunicationHistory history={smsHistory} />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-[#D4AF37] text-xs uppercase tracking-wider">Add Note</Label>
                  <div className="flex gap-2">
                    <Input data-testid="timeline-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Log a call, text, or note..." className="bg-[#0A0A0A] border-[#262626] text-white" />
                    <Button data-testid="timeline-note-submit" onClick={addNote} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">Save</Button>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider">Activity ({timeline.length})</p>
                  {timeline.length === 0 ? (
                    <p className="text-[#A3A3A3] text-sm">No activity yet</p>
                  ) : timeline.map((t) => (
                    <div key={t.id} className="border-l-2 border-[#D4AF37] pl-3 py-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-bold">{EVENT_LABELS[t.event_type] || t.event_type}</span>
                        <span className="text-[#525252] text-[10px]">{fmtETDateTime(t.created_at)}</span>
                      </div>
                      <p className="text-white text-sm mt-0.5">{t.summary}</p>
                      <p className="text-[#525252] text-xs">by {t.actor_name || 'System'}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CustomerEditDialog
        open={!!editCust}
        onOpenChange={(o) => !o && setEditCust(null)}
        customer={editCust}
        onSaved={(updated) => {
          refresh();
          refreshCounts();
          // Keep the timeline drawer in sync with the new fields.
          if (updated) setOpenCust((prev) => (prev && prev.id === updated.id ? { ...prev, ...updated } : prev));
        }}
      />
    </div>
  );
};

export default Customers;
